/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { GlobalLoader } from "@/components/loading/GlobalLoader";
import { ProtectedRoute } from "@/features/auth/ProtectedRoute";
import { RoleProtectedRoute } from "@/features/auth/RoleProtectedRoute";
import {
  administrationAccessRoles,
  projectAdministrationRoles,
  projectTechnicalRoles,
  projectWorkspaceRoles,
  supportPortalRoles,
  teamWorkspaceRoles
} from "@/features/auth/roles";
import { AppShell } from "@/layouts/AppShell";
import { PublicLayout } from "@/layouts/PublicLayout";

const AccountPendingPage = lazy(() =>
  import("@/pages/AccountPendingPage").then((module) => ({ default: module.AccountPendingPage }))
);
const AboutPage = lazy(() =>
  import("@/pages/AboutPage").then((module) => ({ default: module.AboutPage }))
);
const AdministrationPage = lazy(() =>
  import("@/pages/AdministrationPage").then((module) => ({ default: module.AdministrationPage }))
);
const ActivateAccountPage = lazy(() =>
  import("@/pages/ActivateAccountPage").then((module) => ({ default: module.ActivateAccountPage }))
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((module) => ({ default: module.DashboardPage }))
);
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((module) => ({ default: module.LoginPage }))
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage }))
);
const NewProcessPage = lazy(() =>
  import("@/pages/NewProcessPage").then((module) => ({ default: module.NewProcessPage }))
);
const NotificationsPage = lazy(() =>
  import("@/pages/NotificationsPage").then((module) => ({ default: module.NotificationsPage }))
);
const ProjectsPage = lazy(() =>
  import("@/pages/ProjectsPage").then((module) => ({ default: module.ProjectsPage }))
);
const ProjectDetailsPage = lazy(() =>
  import("@/pages/ProjectDetailsPage").then((module) => ({
    default: module.ProjectDetailsPage
  }))
);
const ProjectStudioPage = lazy(() =>
  import("@/pages/ProjectStudioPage").then((module) => ({
    default: module.ProjectStudioPage
  }))
);
const StudioHubPage = lazy(() =>
  import("@/pages/StudioHubPage").then((module) => ({ default: module.StudioHubPage }))
);
const SupportPage = lazy(() =>
  import("@/pages/SupportPage").then((module) => ({ default: module.SupportPage }))
);
const RecoverPasswordPage = lazy(() =>
  import("@/pages/RecoverPasswordPage").then((module) => ({
    default: module.RecoverPasswordPage
  }))
);
const RegisterPage = lazy(() =>
  import("@/pages/RegisterPage").then((module) => ({ default: module.RegisterPage }))
);
const TeamsPage = lazy(() =>
  import("@/pages/TeamsPage").then((module) => ({ default: module.TeamsPage }))
);
const UserProfilePage = lazy(() =>
  import("@/pages/UserProfilePage").then((module) => ({ default: module.UserProfilePage }))
);
const VerifyAccountPage = lazy(() =>
  import("@/pages/VerifyAccountPage").then((module) => ({ default: module.VerifyAccountPage }))
);

function PageFallback() {
  return <GlobalLoader label="Carregando página" />;
}

function lazyElement(element: ReactNode) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

function roleProtectedElement(
  element: ReactNode,
  allowedRoles: Parameters<typeof RoleProtectedRoute>[0]["allowedRoles"]
) {
  return lazyElement(
    <RoleProtectedRoute allowedRoles={allowedRoles}>{element}</RoleProtectedRoute>
  );
}

function LegacyStudioRedirect() {
  const { projectId = "" } = useParams();
  return <Navigate to={`/processos/${projectId}/studio`} replace />;
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: "/entrar", element: lazyElement(<LoginPage />) },
      { path: "/sobre", element: lazyElement(<AboutPage />) },
      { path: "/cadastro", element: lazyElement(<RegisterPage />) },
      { path: "/recuperar-senha", element: lazyElement(<RecoverPasswordPage />) },
      { path: "/verificar-conta", element: lazyElement(<VerifyAccountPage />) },
      { path: "/ativar-conta", element: lazyElement(<ActivateAccountPage />) }
    ]
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/painel" replace /> },
          { path: "/painel", element: roleProtectedElement(<DashboardPage />, projectWorkspaceRoles) },
          { path: "/projetos", element: roleProtectedElement(<ProjectsPage />, projectWorkspaceRoles) },
          {
            path: "/processos/novo",
            element: roleProtectedElement(<NewProcessPage />, projectAdministrationRoles)
          },
          {
            path: "/projetos/:projectId",
            element: roleProtectedElement(<ProjectDetailsPage />, projectWorkspaceRoles)
          },
          { path: "/equipes", element: roleProtectedElement(<TeamsPage />, teamWorkspaceRoles) },
          { path: "/suporte", element: roleProtectedElement(<SupportPage />, supportPortalRoles) },
          { path: "/clientes", element: <Navigate to="/administracao/usuarios" replace /> },
          { path: "/cadastros", element: <Navigate to="/administracao/usuarios" replace /> },
          { path: "/documentos", element: <Navigate to="/projetos" replace /> },
          { path: "/tarefas", element: <Navigate to="/administracao/tickets" replace /> },
          { path: "/relatorios", element: <Navigate to="/administracao/auditoria" replace /> },
          { path: "/mapa", element: <Navigate to="/projetos" replace /> },
          {
            path: "/notificacoes",
            element: roleProtectedElement(<NotificationsPage />, projectWorkspaceRoles)
          },
          { path: "/usuario", element: lazyElement(<UserProfilePage />) },
          {
            path: "/administracao",
            element: roleProtectedElement(<AdministrationPage />, administrationAccessRoles)
          },
          {
            path: "/processos/:projectId/studio",
            element: roleProtectedElement(<ProjectStudioPage />, projectTechnicalRoles)
          },
          { path: "/studio", element: roleProtectedElement(<StudioHubPage />, projectTechnicalRoles) },
          {
            path: "/administracao/projetos/:projectId/studio",
            element: roleProtectedElement(<LegacyStudioRedirect />, projectTechnicalRoles)
          },
          {
            path: "/administracao/:sectionSlug",
            element: roleProtectedElement(<AdministrationPage />, administrationAccessRoles)
          },
          { path: "/conta-pendente", element: lazyElement(<AccountPendingPage />) }
        ]
      }
    ]
  },
  { path: "*", element: lazyElement(<NotFoundPage />) }
]);
